if (!customElements.get('read-more-description')) {
  customElements.define(
    'read-more-description',
    class ReadMoreDescription extends HTMLElement {
      constructor() {
        super();
        this.maxHeight = parseInt(this.dataset.maxHeight) || 100;
        this.maxWords = parseInt(this.dataset.maxWords) || null;
        this.maxChars = parseInt(this.dataset.maxChars) || null;
        this.limitType = this.dataset.limitType || 'height'; // 'height', 'words', or 'chars'
        this.originalContent = '';
        this.truncatedContent = '';
        this.truncatedHtml = '';
        this.init();
      }

      init() {
        const content = this.querySelector('.read-more-content');
        const toggleButton = this.querySelector('.read-more-toggle');
        
        if (!content || !toggleButton) return;

        this.originalContent = content.innerHTML;
        let needsTruncation = false;

        if (this.limitType === 'words' && this.maxWords) {
          needsTruncation = this.setupWordLimit(content);
        } else if (this.limitType === 'chars' && this.maxChars) {
          needsTruncation = this.setupCharLimit(content);
        } else {
          // Default to height-based truncation
          needsTruncation = this.setupHeightLimit(content);
        }

        if (needsTruncation) {
          toggleButton.style.display = 'block';
          toggleButton.addEventListener('click', () => {
            this.toggleContent();
          });
        } else {
          toggleButton.style.display = 'none';
        }
      }

      setupWordLimit(content) {
        const text = content.textContent || content.innerText;
        const words = text.trim().split(/\s+/);
        
        if (words.length > this.maxWords) {
          const truncatedWords = words.slice(0, this.maxWords);
          this.truncatedContent = truncatedWords.join(' ') + '...';
          
          // Preserve HTML structure when possible
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = this.originalContent;
          const truncatedHtml = this.truncateHtmlByWords(tempDiv, this.maxWords);
          
          this.truncatedHtml = truncatedHtml + '...';
          content.innerHTML = this.truncatedHtml;
          content.classList.add('truncated');
          return true;
        }
        return false;
      }

      setupCharLimit(content) {
        const text = content.textContent || content.innerText;
        
        if (text.length > this.maxChars) {
          this.truncatedContent = text.substring(0, this.maxChars) + '...';
          
          // Preserve HTML structure when possible
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = this.originalContent;
          const truncatedHtml = this.truncateHtmlByChars(tempDiv, this.maxChars);
          
          this.truncatedHtml = truncatedHtml + '...';
          content.innerHTML = this.truncatedHtml;
          content.classList.add('truncated');
          return true;
        }
        return false;
      }

      setupHeightLimit(content) {
        if (content.scrollHeight > this.maxHeight) {
          content.style.maxHeight = this.maxHeight + 'px';
          content.style.overflow = 'hidden';
          content.classList.add('truncated');
          return true;
        }
        return false;
      }

      truncateHtmlByWords(element, maxWords) {
        let wordCount = 0;
        let result = '';
        
        const traverse = (node) => {
          if (wordCount >= maxWords) return false;
          
          if (node.nodeType === Node.TEXT_NODE) {
            const words = node.textContent.trim().split(/\s+/).filter(word => word.length > 0);
            const remainingWords = maxWords - wordCount;
            
            if (words.length <= remainingWords) {
              wordCount += words.length;
              return node.textContent;
            } else {
              const truncatedWords = words.slice(0, remainingWords);
              wordCount += truncatedWords.length;
              return truncatedWords.join(' ');
            }
          } else if (node.nodeType === Node.ELEMENT_NODE) {
            const tagName = node.tagName.toLowerCase();
            let elementContent = '';
            
            for (let child of node.childNodes) {
              const childResult = traverse(child);
              if (childResult === false) break;
              elementContent += childResult;
            }
            
            return `<${tagName}>${elementContent}</${tagName}>`;
          }
          
          return '';
        };
        
        for (let child of element.childNodes) {
          const childResult = traverse(child);
          if (childResult === false) break;
          result += childResult;
        }
        
        return result;
      }

      truncateHtmlByChars(element, maxChars) {
        let charCount = 0;
        let result = '';
        
        const traverse = (node) => {
          if (charCount >= maxChars) return false;
          
          if (node.nodeType === Node.TEXT_NODE) {
            const text = node.textContent;
            const remainingChars = maxChars - charCount;
            
            if (text.length <= remainingChars) {
              charCount += text.length;
              return text;
            } else {
              const truncatedText = text.substring(0, remainingChars);
              charCount += truncatedText.length;
              return truncatedText;
            }
          } else if (node.nodeType === Node.ELEMENT_NODE) {
            const tagName = node.tagName.toLowerCase();
            let elementContent = '';
            
            for (let child of node.childNodes) {
              const childResult = traverse(child);
              if (childResult === false) break;
              elementContent += childResult;
            }
            
            return `<${tagName}>${elementContent}</${tagName}>`;
          }
          
          return '';
        };
        
        for (let child of element.childNodes) {
          const childResult = traverse(child);
          if (childResult === false) break;
          result += childResult;
        }
        
        return result;
      }

      toggleContent() {
        const content = this.querySelector('.read-more-content');
        const toggleButton = this.querySelector('.read-more-toggle');
        const showMoreText = this.querySelector('.show-more-text');
        const showLessText = this.querySelector('.show-less-text');
        
        const isExpanded = !content.classList.contains('truncated');
        
        if (isExpanded) {
          // Collapse
          if (this.limitType === 'height') {
            content.style.maxHeight = this.maxHeight + 'px';
            content.style.overflow = 'hidden';
          } else {
            // For word/char limits, restore the truncated HTML
            content.innerHTML = this.truncatedHtml;
          }
          content.classList.add('truncated');
          showMoreText.style.display = 'inline';
          showLessText.style.display = 'none';
        } else {
          // Expand
          if (this.limitType === 'height') {
            content.style.maxHeight = content.scrollHeight + 'px';
            content.style.overflow = 'visible';
          } else {
            // For word/char limits, restore the original HTML
            content.innerHTML = this.originalContent;
          }
          content.classList.remove('truncated');
          showMoreText.style.display = 'none';
          showLessText.style.display = 'inline';
        }
      }
    }
  );
} 